import {
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { ConversationEmptyState } from '@app/components/ConversationEmptyState'
import config from '@app/config'
import { ChatContext } from '@app/context'
import { navigationFeatureEnabled } from '@app/helpers'
import {
  useAppDispatch,
  useConfiguration,
  useConversationActorRole,
  useConversation,
  useConversationID,
} from '@app/hooks'
import { useParticipantLabels } from '@app/hooks/useParticipantLabels'
import { trackOpenParticipantDetails } from '@app/services/tracking'
import { fetchConversationParticipantLabelsThunk } from '@app/store/participantLabels'
import { isImpersonatedB2BSession } from '@app/utils/token/isImpersonatedB2BSession'

import { Loader, Modal } from '@lib/components'

import {
  closeParticipantsModalCallback,
  handleScrollCallback,
  navigateBackCallback,
  openParticipantsModalCallback,
} from './callbacks'
import { Dialog, Header, Join, Reply } from './components'
import type { FollowUpActionKind } from './components/Dialog/FollowUpActions'
import type { ReplyAttachment } from './components/Reply/types'

import { MOBILE_MAX_WIDTH } from './constants'
import {
  getIsReadOnly,
  getIsConversationListEmpty,
} from './helpers'
import styles from './styles.module.scss'
import { useBuyerModeLifecycle } from './useBuyerModeLifecycle'
import { useChat } from './useChat'

import { Sidebar } from '../Sidebar'
import { ContactDetails, Participants } from '../Sidebar/components'
import participantsStyles from '../Sidebar/components/Participants/styles.module.scss'
import type { SidebarParticipant } from '../Sidebar/components/Participants/types'

export const Chat = () => {
  const { t } = useTranslation()
  const { state } = use(ChatContext)

  const navigate = useNavigate()
  const conversation = useConversation()
  const conversationActorRole = useConversationActorRole()
  const conversationId = useConversationID()
  const configuration = useConfiguration()

  const dispatch = useAppDispatch()
  const participantLabelsState = useParticipantLabels()

  const {
    isConversationParticipant,
    loading,
    fetchMessages,
    isStaff,
    isSpamConversation,
  } = useChat()
  const { isBuyer, shouldDisplayLoading } = useBuyerModeLifecycle({
    conversation,
    conversationId,
    isStaff,
    isSpamConversation,
    fetching: state.fetching,
    fetchInitialStateErrorStatus: state.fetchInitialStateErrorStatus,
  })

  const loadingRef = useRef(loading)
  loadingRef.current = loading
  const [isModalOpen, setModalOpen] = useState(false)
  const [inputValue, setInputValue] = useState(new Map<number, string>())
  const [attachments, setAttachments] = useState(
    new Map<number, ReplyAttachment[]>()
  )
  const [selectedParticipant, setSelectedParticipant] =
    useState<SidebarParticipant | null>(null)
  const [activeFollowUpAction, setActiveFollowUpAction] =
    useState<FollowUpActionKind | null>(null)

  const prefillReply = useCallback(
    (message: string) => {
      if (!conversation) {
        return
      }

      setInputValue((previous) => {
        const next = new Map(previous)
        next.set(conversation.id, message)

        return next
      })
    },
    [conversation, setInputValue]
  )
  const openParticipantsModal = useCallback(
    openParticipantsModalCallback(setModalOpen),
    []
  )
  const closeParticipantsModal = useCallback(
    closeParticipantsModalCallback(setModalOpen),
    []
  )
  const openContactDetails = useCallback(
    (participant: SidebarParticipant) => {
      setSelectedParticipant(participant)
      trackOpenParticipantDetails(conversationActorRole)
    },
    [conversationActorRole]
  )
  const closeContactDetails = useCallback(() => {
    setSelectedParticipant(null)
    setActiveFollowUpAction((current) =>
      current === 'callContact' ? null : current
    )
  }, [])
  const handleSelectFollowUpAction = useCallback((action: FollowUpActionKind) => {
    setActiveFollowUpAction(action)
  }, [])

  const isActAs = isImpersonatedB2BSession()
  const isReadOnly =
    isBuyer ||
    getIsReadOnly(isStaff, isActAs, isSpamConversation)

  const showReply = !isReadOnly && isConversationParticipant
  const showJoin = !isReadOnly && !isConversationParticipant
  const currentReplyValue = conversation
    ? inputValue.get(conversation.id) ?? ''
    : ''

  const hasConversations = useMemo(() => {
    return Object.keys(state.conversations).length > 0
  }, [state.conversations])

  const isConversationListEmpty = useMemo(() => {
    return getIsConversationListEmpty(state.conversations, state.fetching)
  }, [state.conversations, state.fetching])

  const isLoadingConversations =
    (state.fetching !== 'SUCCEED' && !hasConversations) ||
    shouldDisplayLoading
  const hasBottomNavigation = navigationFeatureEnabled()
  const chatContainerClassName = cn(styles.container, {
    [styles.withBottomNavigation]: hasBottomNavigation,
  })
  const conversationDetailsDialogClassName = cn(styles.conversationDetailsDialog, {
    [styles.withBottomNavigation]: hasBottomNavigation,
  })

  const handleScroll = useCallback(
    handleScrollCallback(loadingRef, conversation ?? null, fetchMessages),
    [conversation, fetchMessages]
  )

  const navigateBack = useCallback(
    navigateBackCallback(conversationId, navigate, configuration),
    [conversationId, navigate, configuration]
  )

  useEffect(() => {
    if (!conversation || !isStaff || isBuyer) return

    const isLoaded =
      participantLabelsState.loadedByConversationId[conversation.id]
    const isLoading =
      participantLabelsState.loadingByConversationId[conversation.id]
    const hasFailed =
      participantLabelsState.failedByConversationId[conversation.id]

    if (isLoaded || isLoading || hasFailed) {
      return
    }

    dispatch(fetchConversationParticipantLabelsThunk(conversation.id))
  }, [
    conversation?.id,
    isStaff,
    isBuyer,
    participantLabelsState.loadedByConversationId,
    participantLabelsState.loadingByConversationId,
    participantLabelsState.failedByConversationId,
    dispatch,
  ])

  useEffect(() => {
    if (!config.identity.isWebView) return

    const handleNavigateBack = navigateBack as EventListener
    window.addEventListener('navigate_back', handleNavigateBack)

    return () => {
      window.removeEventListener('navigate_back', handleNavigateBack)
    }
  }, [navigateBack])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > MOBILE_MAX_WIDTH && isModalOpen) {
        closeParticipantsModal()
      }
    }
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [closeParticipantsModal, isModalOpen])

  useEffect(() => {
    closeParticipantsModal()
  }, [closeParticipantsModal, conversationId])

  useEffect(() => {
    setSelectedParticipant(null)
    setActiveFollowUpAction(null)
  }, [conversationId])

  useEffect(() => {
    if (!conversation) {
      setActiveFollowUpAction(null)
      return
    }

    if (activeFollowUpAction === null || activeFollowUpAction === 'callContact') {
      return
    }

    if (!currentReplyValue.trim()) {
      setActiveFollowUpAction(null)
    }
  }, [activeFollowUpAction, conversation, currentReplyValue])

  if (isLoadingConversations) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    )
  }

  if (!conversation || isConversationListEmpty) {
    return (
      <ConversationEmptyState mode="chat" isEmpty={isConversationListEmpty} />
    )
  }

  return (
    <>
      <div className={chatContainerClassName}>
        <div className={styles.dialogWrapper}>
          <Header callbackFn={openParticipantsModal} />
          <div
            id="conversationDetailsDialog"
            className={conversationDetailsDialogClassName}
            onScroll={handleScroll}
          >
            <div className={styles.dialog}>
              <div className={styles.dialogContent}>
                <Dialog
                  conversation={conversation}
                  loading={loading}
                  isStaff={isStaff}
                  isSpamConversation={isSpamConversation}
                  onOpenContactDetails={openContactDetails}
                  onPrefillReply={prefillReply}
                  activeFollowUpAction={activeFollowUpAction}
                  onSelectFollowUpAction={handleSelectFollowUpAction}
                />
              </div>
            </div>
          </div>
          {showReply && (
            <Reply
              conversationId={conversation.id}
              inputValue={inputValue}
              setInputValue={setInputValue}
              attachments={attachments}
              setAttachments={setAttachments}
              activeFollowUpAction={activeFollowUpAction}
            />
          )}
          {showJoin && <Join />}
        </div>
        <Sidebar
          conversation={conversation}
          onParticipantSelect={openContactDetails}
        />
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeParticipantsModal}
        title={t('Chat.Sidebar.Title')}
        isFullScreen={true}
        showBottomCloseButton={false}
      >
        <div className={styles.popup_participants}>
          <Participants
            conversation={conversation}
            onParticipantSelect={openContactDetails}
          />
        </div>
      </Modal>

      <Modal
        className={participantsStyles.modal}
        headerClassName={participantsStyles.modalHeader}
        isOpen={Boolean(selectedParticipant)}
        onClose={closeContactDetails}
        showBottomCloseButton={false}
        title={t('Chat.Contact.Details')}
      >
        {selectedParticipant && (
          <ContactDetails participant={selectedParticipant} />
        )}
      </Modal>
    </>
  )
}
